import Vue                                from "vue";
import { mapGetters }                     from "vuex";
import vSelect                            from "vue-select";
import _ from "lodash";
import { 
    EntitlementsService, 
    CurrencyService
}                                         from "@/services/";
import { 
    formatDateTime, 
    stringHelper, 
    paginationMixin 
}                                         from "@/components/mixins";
import { LoadingSpinner, DnFunctionButton }                 from "@/components/Elements";
import { default as CcrRequestForm }      from "@/components/ReverseLogistics/Entitlements/CcrRequestForm.vue";
import { Nullable }                       from "@/types";
import { DownloadJsonToCsv } from "@/components/Elements/DownloadJsonToCsv"
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

export default Vue.extend({
    name: "Entitlements",
    components: {
        LoadingSpinner,
        DnFunctionButton,
        vSelect,
        CcrRequestForm,
        DownloadJsonToCsv,
        DnTable
    },
    mixins: [
        formatDateTime, 
        stringHelper, 
        paginationMixin
    ],
    computed: {
        ...mapGetters([
            "user",
            "getContentItem",
            "availableDealerCodes",
            "activeAccountId"
        ]),
        hasAccessToAllBranches() {
            return (
              this.availableDealerCodes.length > 1 && this.user.DealerRange !== "*"
            );
        },
        entitlementsForDownload(){
            var self = this;
             var entitlementsForDownload = this.filteredEntitlements.map(function(x){
                 return {
                     PartNumber: x.OemPartCode,
                     Description: x.Description,
                     Commodity: x.PicCode.Description,
                     InvoiceNumber: x.InvoiceNumber,
                     InvoiceDate: self.formatDateTime(x.InvoiceDate).trim(),
                     ExpirationDate: self.formatDateTime(x.ExpirationDate).trim(),
                     OrderType: x.OrderType,
                     Dealer: x.ShipTo,
                     Quantity: x.Quantity,
                     ExtendedValue: self.formatCurrency(x.Price, x.Account.PricingCurrencyCode).trim()
                 }
             });
             return entitlementsForDownload;
         },
        filteredEntitlements() {
            var self = this;
            let filteredByExpiredDate = this.entitlements;
            
            // Get either expired or unexpired only
            filteredByExpiredDate = filteredByExpiredDate.filter((entitlement) => {
                return self.DateIsInTheFuture(entitlement.ExpirationDate) !== this.showExpiredOnly 
            });

            if( this.textFilter !== null){   
                filteredByExpiredDate = filteredByExpiredDate.filter((entitlement) => {
                    return this.filterByText(entitlement);
                });
             }

            if(!this.selectedCommodity ||
                this.selectedCommodity === "--- No Selection ---"){
                // if search term present, filter more
                if (this.isFromSearch) {
                    return this.filterByOemPartCode(filteredByExpiredDate);
                }
                // else return
                return filteredByExpiredDate;
            }

            var filteredEntitlements = filteredByExpiredDate.filter((x: any) => 
                x.PicCode.Description === self.selectedCommodity
            );
            
            // if search term present, filter more
            if (this.isFromSearch) {
                return this.filterByOemPartCode(filteredByExpiredDate);
            }

            // final fallback
            return filteredEntitlements;
        },
        addToCCrEntitlements(){
            if (this.filteredEntitlements !== undefined) {
                var filteredAndSelectedEntitlements = this.filteredEntitlements.filter(x => x.selected);
                return filteredAndSelectedEntitlements;
            }
            return {};
        },
        disableAddToCcrButton(){
            return this.IsNullOrUndefined(this, 'addToCCrEntitlements.length') || this.addToCCrEntitlements.length < 1;
        },
    },
    data: () => ({
        entitlements: [],
        entitlementsDataColumns:[
            { key: "selected", label: "", sortable: false},
            { key: "OemPartCode", label: "Part Number", sortable: true },
            { key: "Description", label: "Description", sortable: true },
            { key: "Commodity", label: "Commodity", sortable: true },
            { key: "InvoiceNumber", label: "Invoice Number", sortable: true },
            { key: "InvoiceDate", label: "Invoice Date", sortable: true },
            { key: "ExpirationDate", label: "Expire Date", sortable: true },
            { key: "OrderType", label: "Order Type", sortable: true },
            { key: "OrderNumber", label: "Order Number", sortable: true },
            { key: "ShipTo", label: "Dealer", sortable: true },
            { key: "Quantity", label: "Qty", sortable: true },
            { key: "Price", label: "Extended Value", sortable: true },
        ],
        sortBy: "InvoiceDate",
        sortDesc: true,
        isLoadingEntitlements: true,
        isLoadingAddToCcr: false,
        commodities: [],
        commodityNames: [],
        selectedCommodity: null,
        showExpiredOnly: false,
        oemPartCode: "",
        isFromSearch: false,
        textFilter: null,
        prevTextFilter: null,
    }),
    created() {
        window.scroll(0,0)
        this.loadEntitlements()     

        if (this.$route.query["showExpiredOnly"] !== undefined) {
            this.showExpiredOnly = this.$route.query["showExpiredOnly"];
        }

        let routeQuery = this.$route.query["oemPartCode"];
        this.oemPartCode = routeQuery !== undefined ? `${routeQuery}R` : "";
        this.isFromSearch = this.oemPartCode !== "";
    },
    methods: {
        loadEntitlements(){
            let allAccountIds = this.availableDealerCodes.map(x => x.AccountId)
            // if selected dealer is "All Branches", load entitlements for all accounts

            if(this.user.DealerRange !== '*'){
                allAccountIds.forEach(x => this.getEntitlements(x, this.selectedCommodity, false));
            }
            else{
                this.getEntitlements(this.activeAccountId, this.selectedCommodity, false);
            }
            
        },
        async getEntitlements(accountId:Number, PicCodeSubId: any, clearExisting: Boolean){
            this.isLoadingEntitlements = true
            if( clearExisting === true){
                this.entitlements = [];
            }
            let requestParams = {
                AccountId: accountId,
                AccountIdOverride: accountId,
                IsPending: false,
                PicCodeSubId,
                IncludeExpired: true
            }
            EntitlementsService.GetEntitlements(requestParams)
            .then(response => {
                let newEntitlements = [...response.data];

                let entIds = this.entitlements.map(ent => ent.Id);
                let uniqueEntIds = _.uniq(entIds);

                for(let i = 0; i < newEntitlements.length; i++){
                    let entitlement = newEntitlements[i]
                    
                    if( !uniqueEntIds.includes(entitlement.Id)){
                        this.entitlements.push(entitlement);
                        uniqueEntIds.push(entitlement.Id);
                    }
                }

                this.GetCommodities();
                this.GetCommodityNames();

                this.isLoadingEntitlements = false;
            })
            .catch(() => {
                this.$store.commit("pushTimedMessage", {
                    header: "An error occurred retrieving your entitlements.",
                    IsError: true
                  })
            });              
        },
        addToCCr(){
            if( this.IsNullOrUndefined(this, "addToCCrEntitlements.length") || this.addToCCrEntitlements.length < 1){
                return;
            }
            this.setRecentComponentData();
            this.$router.push({name: "CcrRequestForm"});
        },
        formatCurrency(amount: Nullable<number>, formatCurrency: any): string | null {
            return amount
                ? CurrencyService.formatCurrency(amount, formatCurrency)
                : "";
        },
        getEntitlementTableRowClasses(item, type){
            var classes = "ecc__table-tr ";
            if(!item)
            {
                return classes;
            }

            var isExpired = this.DateIsInThePast(item.ExpirationDate);

            if(isExpired){
                classes += "ecc__table-tr--expired";
            }
            return classes;
        },
        GetCommodities(){
            if( this.commodities.length === 0){
                this.commodities.push({PicCodeId: null, CommodityName: "--- No Selection ---"})                 
            }
            this.commodities.push(...this.entitlements.map(x => ({PicCodeId: x.PicCode.Id, CommodityName: x.PicCode.Description})))
        },
        GetCommodityNames(){
            let distinctCommodityNames:any = []
            for(const item of this.commodities){
                if( !distinctCommodityNames.includes(item.CommodityName)){
                    distinctCommodityNames.push(item.CommodityName)
                }
            }            
            this.commodityNames = distinctCommodityNames.sort();
        },
        setRecentComponentData() {
            this.$store.commit("SET_RECENT_COMPONENT", {
                name: "Entitlements",
                data: {
                    entitlements: this.addToCCrEntitlements
                },
            });
        },
        showExpired() {
            this.showExpiredOnly = !this.showExpiredOnly;
        },
        filterByOemPartCode(entitlements) {
            let filteredItems = [{}];
            filteredItems.splice(0, 1); // drop empty element

            entitlements.forEach(entitlement => {
                if (entitlement.OemPartCode === this.oemPartCode) {
                    filteredItems.push(entitlement);
                }
            })
            return filteredItems;
        },
        filterByText (row) {

            var textFields: string[] = [];
           
            textFields.push(row.OemPartCode);
            textFields.push(row.Description);
            textFields.push(row.PicCode.Description);
            textFields.push(row.InvoiceNumber);
            if(row.ShipTo != null){
                textFields.push(row.ShipTo.toString());
            }
            if(row.Quantity != null){
                textFields.push(row.Quantity.toString());
            }
              
            var price = this.formatCurrency(row.Price, row.Account.PricingCurrencyCode).trim()
            textFields.push(price);
            textFields = textFields.map((x) => x.toLowerCase());

            var dateFields: string[] = [];

            var invoiceDate = this.formatDateTime(row.InvoiceDate).trim();
            dateFields.push(invoiceDate);
            var expirationDate = this.formatDateTime(row.ExpirationDate).trim();
            dateFields.push(expirationDate);

            var self = this;
            var match = false;
            textFields.forEach(function(textField) {
                if(textField.includes(self.textFilter)){
                    match = true;
                    return;
                }
            });

            dateFields.forEach(function(dateField) {
                var value = self.RemoveDateSpecialChars(dateField);
                var text  = self.RemoveDateSpecialChars(self.textFilter);
                if(value.includes(text)){
                    match = true;
                    return;
                }   
            });

            return match;
          }
    }
})
 